import { useEffect, useRef, useState } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { jwtDecode } from 'jwt-decode';
import 'react-toastify/dist/ReactToastify.css';
import './styles/App.css';

import { Box, Button, Typography } from '@mui/material';
import { getMainStyles } from './styles/styles';
import { useTheme } from '@mui/material/styles';

import Sidebar from './pages/sidebar/index';
import Navbar from './pages/navbar/index';
import { useCredit } from './context/CreditContext';
import ProtectedRoutes from './Routes/ProtectedRoutes';
import { routesConfig } from './Routes/Routes';

import Authentication from './Auth/Authentication';
import InviteSignIn from './Auth/InviteSignIn';
import Authorized from './Auth/Authorized';
// import useAuthCheck from './Auth/AuthTokenCheck';

import { useCollection } from './context/CollectionContext';
import { useAppContext } from './context/AppContext';
import CollectionTypesService from './Api/Services/collection/collectionTypesService';
import { APPLICATION_CODE_PLURAL, OTHER_PLURAL_ID } from './utils/constants';
import RootUserHome from './pages/RootUser';

function App() {
  const location = useLocation()
  const theme = useTheme()
  const dispatch = useDispatch();
  const navigate = useNavigate()
  const mainStyles = getMainStyles(theme);

  const { shouldShowCreditWarning, warningMessage } = useCredit();

  const { selectedWorkspace, selectedProject } = useAppContext();

  const {
    centralLoadingFlags, setCentralLoadingFlag,
    collectionTypeList, setCollectionTypeList,
    collectionPublishedList, setCollectionPublishedList,
    fieldSetList, setFieldSetList,
    fieldSetListPublished, setFieldSetListPublished
  } = useCollection();

  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [parsedDPODToken, setParsedDPODToken] = useState(null)
  const [open, setOpen] = useState(true);

  const fetchedForProjectRef = useRef({})

  // useAuthCheck(isAuthenticated)

  useEffect(() => {
    handleAuthentication();
    //eslint-disable-next-line
  }, [navigate]);

  const handleAuthentication = async () => {
    const jwtIdToken = localStorage.getItem('dpod-token');
    if (location.pathname !== "/authorized") {
      if (jwtIdToken) {
        try {
          const parsedToken = jwtDecode(jwtIdToken)
          setParsedDPODToken(parsedToken)
          setIsAuthenticated(true);
          if (location.pathname === "/login" || location.pathname === "/invite-login") {
            navigate('/')
          }
        } catch (error) {
          console.error("Failed to parse token:", error);
          localStorage.clear();
          sessionStorage.clear();
          dispatch({ type: 'RESET_ALL' });
          setIsAuthenticated(false);
          navigate('/login');
        }
      } else {

        setIsAuthenticated(false);

        const currentPath = location.pathname;
        const skipPaths = ['/invite-login', '/root-user', '/login'];

        if (location.search) {
          sessionStorage.setItem("externalParams", location.search);
        }

        if (!skipPaths.includes(currentPath)) {
          navigate('/login');
        }

      }
    }
  };

  const chatbotTokenClear = () => {
    // Chatbot token
    localStorage.removeItem("service-agent-user-token")
    localStorage.removeItem("agent-user-token")
    localStorage.removeItem("session_id")
    localStorage.removeItem("analytics_base_url")
  }

  useEffect(() => {
    if (parsedDPODToken && selectedWorkspace && selectedProject) {
      fetchedForProjectRef.current = {}
      setCollectionTypeList([])
      setCollectionPublishedList([])
      setFieldSetList([])
      setFieldSetListPublished([])
      chatbotTokenClear()
      //eslint-disable-next-line
    }
  }, [parsedDPODToken, selectedWorkspace, selectedProject]);

  const fetchCollectionTypes = (force = true) => {
    if (parsedDPODToken && selectedProject) {
      const currentProjectId = selectedProject.payload.__auto_id__
      if (!force && fetchedForProjectRef.current.collectionTypes === currentProjectId) return
      fetchedForProjectRef.current.collectionTypes = currentProjectId
      setCentralLoadingFlag(prev => ({ ...prev, collectionTypes: true }))

      const accID = parsedDPODToken.root_account_id
      const subscriberId = parsedDPODToken.subscriber_id
      const subscriptionId = parsedDPODToken.subscription_id
      const schemaId = currentProjectId

      CollectionTypesService
        .getAllCollectionTypesForCoach(accID, subscriptionId, subscriberId, schemaId)
        .then(res => {
          const responseData = res.data ?? [];
          const filtered_data = (responseData || [])?.filter(item => item?.system_predefined === false) ?? []
          setCollectionTypeList(filtered_data)
        })
        .catch(err => {
          console.log("Error occured while fetching all collection type list for a coach", err)
          setCollectionTypeList()
        })
        .finally(() => {
          setCentralLoadingFlag(prev => ({ ...prev, collectionTypes: false }))
        })
    }
  }

  const fetchPublishedCollection = async () => {
    if (parsedDPODToken && selectedProject && !centralLoadingFlags.publishedCollection) {

      setCentralLoadingFlag(prev => ({ ...prev, publishedCollection: true }))
      try {
        const accID = parsedDPODToken.root_account_id
        const subscriberId = parsedDPODToken.subscriber_id
        const subscriptionId = parsedDPODToken.subscription_id
        const schemaId = selectedProject.payload.__auto_id__

        let publishedList = []
        let lastKey = null
        let counter = 0
        const limit = 100

        do {
          const res = await CollectionTypesService.getPublishedCollectionTypesPagination(accID, subscriptionId, subscriberId, schemaId, lastKey, limit)
          const latest_data = res?.data?.items ?? [];
          publishedList = [...publishedList, ...latest_data]

          if (res.data.last_evaluated_key == null) {
            lastKey = null
          }
          else if (typeof res.data.last_evaluated_key === "object")
            lastKey = encodeURIComponent(JSON.stringify(res.data.last_evaluated_key))
          else
            lastKey = null
          counter++
        }

        while (lastKey !== null && counter <= 5);
        const versionMap = {}
        const filteredList = []
        let pos = 0

        for (const item of publishedList) {
          if (item.base_defition_entity_key in versionMap) {
            if (versionMap[item.base_defition_entity_key].version >= parseInt(item.base_defition_entity_version))
              continue
            filteredList[versionMap[item.base_defition_entity_key].index] = item
            versionMap[item.base_defition_entity_key].version = parseInt(item.base_defition_entity_version)
          }
          else {
            versionMap[item.base_defition_entity_key] = {
              index: pos,
              version: parseInt(item.base_defition_entity_version)
            }
            filteredList[pos] = item
            pos += 1
          }
        }
        const filteredListFinal = filteredList.filter(x => (x.api_prural_id === OTHER_PLURAL_ID.websiteRequest || x.api_prural_id === APPLICATION_CODE_PLURAL.lookups || (x.system_predefined === false && x.system_utility === false && x.soft_deleted === false)))
        setCollectionPublishedList(filteredListFinal)

      } catch (err) {
        console.log("Error occured while fetching published collection type list for a coach", err)
        setCollectionPublishedList([])
      } finally {
        setCentralLoadingFlag(prev => ({ ...prev, publishedCollection: false }))
      }
    }
  }

  const fetchFieldSets = (force = true) => {
    if (parsedDPODToken && selectedProject) {
      const currentProjectId = selectedProject.payload.__auto_id__
      if (!force && fetchedForProjectRef.current.fieldSets === currentProjectId) return
      fetchedForProjectRef.current.fieldSets = currentProjectId
      setCentralLoadingFlag(prev => ({ ...prev, fieldSets: true }))

      const accID = parsedDPODToken.root_account_id
      const subscriberId = parsedDPODToken.subscriber_id
      const subscriptionId = parsedDPODToken.subscription_id
      const schemaId = currentProjectId

      CollectionTypesService
        .getAllFieldSetsForCoach(accID, subscriptionId, subscriberId, schemaId)
        .then(res => { setFieldSetList(res.data) })
        .catch(err => {
          console.log("Error occured while fetching all collection type list for a coach", err)
          setFieldSetList([])
        })
        .finally(() => {
          setCentralLoadingFlag(prev => ({ ...prev, fieldSets: false }))
        })
    }
  }

  const fetchPublishedFieldset = async (force = true) => {
    try {
      if (parsedDPODToken && selectedProject) {
        const currentProjectId = selectedProject.payload.__auto_id__
        if (!force && fetchedForProjectRef.current.publishedFieldset === currentProjectId) return
        fetchedForProjectRef.current.publishedFieldset = currentProjectId

        setCentralLoadingFlag(prev => ({ ...prev, publishedFieldset: true }))

        const accID = parsedDPODToken.root_account_id
        const subscriberId = parsedDPODToken.subscriber_id
        const subscriptionId = parsedDPODToken.subscription_id
        const schemaId = currentProjectId

        let publishedList = []
        let lastKey = null
        let counter = 0
        const limit = 50

        try {

          do {
            const res = await CollectionTypesService.getPublishedFieldSets(accID, subscriptionId, subscriberId, schemaId, lastKey, limit)

            const latest_data = res?.data?.items ?? [];
            publishedList = [...publishedList, ...latest_data]

            if (res.data.last_evaluated_key == null) {
              lastKey = null
            } else if (typeof res.data.last_evaluated_key === "object")
              lastKey = encodeURIComponent(JSON.stringify(res.data.last_evaluated_key))
            else
              lastKey = null
            counter++
          }

          while (lastKey !== null && counter <= 5);
          const versionMap = {}
          const filteredList = []
          let pos = 0

          for (const item of publishedList) {
            if (item.base_defition_entity_key in versionMap) {
              if (versionMap[item.base_defition_entity_key].version >= parseInt(item.base_defition_entity_version))
                continue
              filteredList[versionMap[item.base_defition_entity_key].index] = item
              versionMap[item.base_defition_entity_key].version = parseInt(item.base_defition_entity_version)
            }
            else {
              versionMap[item.base_defition_entity_key] = {
                index: pos,
                version: parseInt(item.base_defition_entity_version)
              }
              filteredList[pos] = item
              pos += 1
            }
          }
          setFieldSetListPublished(filteredList)
        }
        catch (err) {
          console.log("Error occured while fetching published collection type list for a coach", err)
          setFieldSetListPublished([])
        }
      }
    } catch (err) {
      console.log("Error occured while fetching published collection type list for a coach", err)
      setFieldSetListPublished([])
    } finally {
      setCentralLoadingFlag(prev => ({ ...prev, publishedFieldset: false }))
    }
  }

  const tostAlert = (msg, mode) => {
    if (mode === 'success')
      toast.success(msg)
    else if (mode === 'info')
      toast.info(msg)
    else if (mode === 'warning')
      toast.warn(msg)
    else if (mode === 'error')
      toast.error(msg)
  }

  const showCreditWarning = shouldShowCreditWarning();

  return (

    <div style={mainStyles.appContainer}>

      {/* Skip to main content link for keyboard/screen reader users */}
      <a
        href="#main-content"
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          zIndex: 10000,
        }}
        onFocus={(e) => {
          e.target.style.position = 'fixed';
          e.target.style.top = '10px';
          e.target.style.left = '10px';
          e.target.style.width = 'auto';
          e.target.style.height = 'auto';
          e.target.style.padding = '8px 16px';
          e.target.style.backgroundColor = '#0B51C5';
          e.target.style.color = '#FFFFFF';
          e.target.style.borderRadius = '4px';
          e.target.style.textDecoration = 'none';
          e.target.style.fontSize = '14px';
          e.target.style.fontWeight = '600';
        }}
        onBlur={(e) => {
          e.target.style.position = 'absolute';
          e.target.style.left = '-9999px';
          e.target.style.width = '1px';
          e.target.style.height = '1px';
        }}
      >
        Skip to main content
      </a>

      <ToastContainer position="top-center" autoClose={3500} hideProgressBar newestOnTop={false} closeOnClick
        rtl={false} pauseOnFocusLoss draggable pauseOnHover theme='colored' />

      {/* Live region for screen reader announcements */}
      <div aria-live="polite" aria-atomic="true" style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }} id="sr-announcements" />

      {!isAuthenticated
        ?
        (<Routes>
          <Route path='/login' element={<Authentication />}></Route>
          <Route path='/invite-login' element={<InviteSignIn />}></Route>
          <Route path='/authorized' element={<Authorized />}></Route>
          <Route path='/root-user' element={<RootUserHome />} />
        </Routes>)
        :
        (<>

          {showCreditWarning && (
            <Box sx={{ ...mainStyles.creditContainer }} role="alert">
              <Typography>
                {warningMessage}
              </Typography>

              <Button
                sx={mainStyles.addBtn}
                onClick={() => navigate("/user/billing")}
              >
                Upgrade
              </Button>
            </Box>
          )}

          <div style={mainStyles.layoutContainer(showCreditWarning)}>

            <div style={mainStyles.sidebarContainer(showCreditWarning)}>
              <Sidebar />
            </div>

            <div style={mainStyles.rightSection}>

              <div style={mainStyles.mainNavbar}>
                <Navbar />
              </div>

              <main id="main-content" style={mainStyles.mainContent} tabIndex={-1}>
                <Routes>
                  <Route element={
                    <ProtectedRoutes

                      isAuthenticated={isAuthenticated}

                      centralLoadingFlags={centralLoadingFlags}
                      tostAlert={tostAlert}
                      selectedUser={parsedDPODToken}
                      emailVerified={true}

                      collectionTypeList={collectionTypeList}
                      collectionPublishedList={collectionPublishedList}
                      fetchCollectionTypes={fetchCollectionTypes}
                      fetchPublishedCollection={fetchPublishedCollection}

                      fieldSetList={fieldSetList}
                      fieldSetListPublished={fieldSetListPublished}
                      fetchFieldSets={fetchFieldSets}
                      fetchPublishedFieldset={fetchPublishedFieldset}

                      staffList={[]}

                      open={open}
                      setOpen={setOpen}

                    />}
                  >
                    {routesConfig.map((route, index) => (
                      <Route key={index} path={route.path} element={route.element} />
                    ))}
                  </Route>
                </Routes>
              </main>

            </div>

          </div>
        </>)
      }

    </div>

  );
}

export default App;